/**
 * useNarration Hook
 * Easy-to-use React hook for voice narration
 */

import { useEffect, useCallback, useState } from 'react';
import { voiceManager } from '../lib/voiceManager';
import { getNarration } from '../lib/narrationScripts';
import { useLanguage } from '../contexts/LanguageContext';

interface UseNarrationOptions {
  scriptId: string;
  autoPlay?: boolean;
  onComplete?: () => void;
}

export function useNarration({ scriptId, autoPlay = false, onComplete }: UseNarrationOptions) {
  const { language } = useLanguage();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const speak = useCallback(async () => {
    const script = getNarration(scriptId);
    if (!script) {
      console.warn(`Narration script not found: ${scriptId}`);
      return;
    }

    setIsPlaying(true);
    setIsPaused(false);

    await voiceManager.speak({
      text: script[language],
      emotion: script.emotion,
      language,
      pauseAfter: script.pauseAfter,
      onComplete: () => {
        setIsPlaying(false);
        onComplete?.();
      },
    });
  }, [scriptId, language, onComplete]);

  const stop = useCallback(() => {
    voiceManager.stop();
    setIsPlaying(false);
    setIsPaused(false);
  }, []);

  const pause = useCallback(() => {
    voiceManager.pause();
    setIsPaused(true);
  }, []);

  const resume = useCallback(() => {
    voiceManager.resume();
    setIsPaused(false);
  }, []);

  useEffect(() => {
    if (autoPlay) {
      speak();
    }

    return () => {
      voiceManager.stop();
    };
  }, [autoPlay, speak]);

  return {
    speak,
    stop,
    pause,
    resume,
    isPlaying,
    isPaused,
  };
}
